import { ChangeEvent, useState } from "react";
import { Checkbox, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import {
  DialogFrame,
  useDialog,
} from "../../../components/Dialogs/dialog-base";
import { CustomInput } from "../../../components/Input/CustomInput";
import { useOrderActionHandler } from "../hooks/useOrderActionsHandler";
import { CartItemDesign } from "../models/CartItem";
import { DesignDetails } from "./DesignDetails";
import { LogoTextEditor } from "./LogoTextEditor";
import { OrderLineViewDesignPrint } from "./OrderLineViewDesignPrint";
import { CasingOption, transformStringCase } from "../../../shared/functions/casing";

interface OrderLineViewDesignProps {
  orderline: CartItemDesign;
}

export function OrderLineViewDesign({ orderline }: OrderLineViewDesignProps) {
  const dialogOptions = useDialog();
  const [customerFillsData, setCustomerFillsData] = useState(
    orderline.customerEntersNamesAndNumbers ?? false,
  );
  const [customerEmail, setCustomerEmail] = useState(
    orderline.customerEntersNamesAndNumbersEmail ?? "",
  );
  const { handleDesignInstanceUpdateStorage } = useOrderActionHandler();
  const [casing, setCasing] = useState(
    orderline.casing ?? CasingOption.UserDefined,
  );

  const designLogos = orderline.design.designLogos;
  const excludedDeletedDesignLogos = orderline.excludedDeletedDesignLogos ?? [];
  const canCustomerFillData = !!designLogos.find((l) =>
    ["NameLogo", "NumberLogo"].includes(l.logo.logoType),
  );

  const toggleCustomerFillsData = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCustomerFillsData(checked);
    if (checked) dialogOptions.openDialog();
    else setCustomerEmail("");
    handleDesignInstanceUpdateStorage({
      ...orderline,
      customerEntersNamesAndNumbersEmail: "",
      customerEntersNamesAndNumbers: false,
    });
  };

  const updateCustomerFillsData = () => {
    // NOTE: Beautiful email regex:
    if (/.+@.+\..+/.test(customerEmail)) {
      dialogOptions.closeDialog();
      handleDesignInstanceUpdateStorage({
        ...orderline,
        customerEntersNamesAndNumbersEmail: customerEmail,
        customerEntersNamesAndNumbers: customerFillsData,
      });
    }
  };

  const updateCasing = (casing: CasingOption) => {
    setCasing(casing);
    const transformedInstances = orderline.instances.map((instance) => ({
      ...instance,
      logos: instance.logos.map((logo) =>
        logo.logoVariant === "name"
          ? {
              ...logo,
              value: transformStringCase(String(logo.value ?? ""), casing),
            }
          : logo
      ),
    }));
    handleDesignInstanceUpdateStorage({
      ...orderline,
      casing,
      instances: transformedInstances,
    });
  };

  const excludedDeletedLogoCount = excludedDeletedDesignLogos.length;
  const excludedDeletedLogoSerials = excludedDeletedDesignLogos
    .map((dl) => dl.logo.serial)
    .filter((s): s is string => !!s);
  const formatSerialList = (serials: string[]): string => {
    if (serials.length === 0) return "";
    if (serials.length === 1) return serials[0];
    return `${serials.slice(0, -1).join(", ")} and ${serials[serials.length - 1]}`;
  };
  const tooltipText =
    excludedDeletedLogoSerials.length > 0
      ? `Logo ${formatSerialList(excludedDeletedLogoSerials)} ${excludedDeletedLogoSerials.length === 1 ? "was" : "were"} marked as deleted and will not be produced or included in this order.`
      : "These logos were marked as deleted and will not be produced or included in this order.";

  return (
    <div id="main-box" className="flex-1 flex space-x-10">
      <div id="left-box" className="space-y-2 flex-1">
        {excludedDeletedLogoCount > 0 && (
          <Tooltip
            title={tooltipText}
            placement="top"
            slotProps={{
              tooltip: { sx: { fontSize: "0.8rem", maxWidth: 320 } },
            }}
          >
            <div className="flex items-center gap-x-2 rounded border border-yellow-300 bg-yellow-50 p-2 text-sm text-yellow-800">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                {excludedDeletedLogoCount} deleted logo
                {excludedDeletedLogoCount === 1 ? "" : "s"} excluded from this
                design (hover for details)
              </span>
            </div>
          </Tooltip>
        )}
        <div className="flex-1">
          <DesignDetails orderline={orderline} />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase mb-2">PRINTS</p>
          <div className="overflow-x-hidden overflow-y-scroll pr-1 h-[450px] grid grid-cols-2 gap-x-3 gap-y-4">
            {designLogos.map((designLogo) => (
              <OrderLineViewDesignPrint
                key={designLogo.id}
                designLogo={designLogo}
                orderline={orderline}
              />
            ))}
            {excludedDeletedDesignLogos.map((designLogo) => (
              <OrderLineViewDesignPrint
                key={`deleted-${designLogo.id}`}
                designLogo={designLogo}
                orderline={orderline}
                isDeleted
              />
            ))}
          </div>
        </div>
      </div>
      <div id="right-box" className="flex-1 flex flex-col mb-8">
        <div
          className={`${customerFillsData ? "opacity-50 pointer-events-none" : ""}`}
        >
          <LogoTextEditor
            orderlineDesign={orderline}
            casing={casing}
            setCasing={updateCasing}
          />
        </div>
        {canCustomerFillData && (
          <div className="flex flex-row items-center justify-end pt-2">
            <Checkbox
              checked={customerFillsData}
              onChange={toggleCustomerFillsData}
            />
            CUSTOMER WRITES NAMES AND NUMBERS
          </div>
        )}
      </div>

      <DialogFrame dialogOption={dialogOptions}>
        <div className="flex flex-col space-y-4 p-4">
          <p className="text-left text-base font-bold">
            Design (Names and Numbers)
          </p>
          <p className="text-left">
            Type the Email you with to fill out names and numbers
          </p>
          <div className="flex flex-col justify-center space-y-4">
            <CustomInput
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
            <div className="flex flex-row justify-between space-x-10">
              <SecondaryButton onClick={dialogOptions.closeDialog}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                className="font-bold"
                onClick={updateCustomerFillsData}
              >
                Send
              </PrimaryButton>
            </div>
          </div>
        </div>
      </DialogFrame>
    </div>
  );
}
